/**
 * Schema API Service
 *
 * API client for LinkML schema management endpoints.
 * Handles schema listing, validation, and prompt generation.
 */

// Always use relative URLs - Caddy proxy handles routing to backend
const API_BASE_URL = '';

// ============================================================================
// Types
// ============================================================================

export interface SchemaAPIResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

export interface BuiltinSchemaInfo {
  name: string;
  title?: string;
  description?: string;
  class_count: number;
  enum_count: number;
  latest_version?: string;
  available_versions?: string[];
}

export interface TemplateVersionInfo {
  version: string;
  created_at?: string;
  is_latest: boolean;
}

export interface GlobalSchemaTemplate {
  name: string;
  title: string | null;
  description: string | null;
  tags: string[];
  latest_version: string;
  versions: TemplateVersionInfo[];
  class_count: number;
  enum_count: number;
}

export interface TemplateUsage {
  template_name: string;
  version: string;
  organizations: {
    id: string;
    name: string;
    assigned_at: string;
  }[];
}

export interface AssignTemplateRequest {
  org_ids: string[];
  template_name: string;
  version: string;
}

export interface CreateTemplateRequest {
  name: string;
  title: string;
  tags: string[];
  description: string;
  yaml_content: string;
  version: string;
}

export interface CreateTemplateVersionRequest {
  version: string;
  yaml_content: string;
}

export interface ExtendSchemaRequest {
  base_template_name: string;
  base_template_version: string;
  extension_yaml: string;
}

export interface BuiltinSchemaDetail {
  name: string;
  yaml_content: string;
  info: {
    title?: string;
    description?: string;
  };
  classes: Record<string, unknown>;
  enums: Record<string, unknown>;
}

export interface OrganizationSchemaResponse {
  schema_hash: string;
  schema_version: string;
  schema_type: string;
  schema_name?: string;
  updated_at?: string;
  classes: string[];
  enums: string[];
  yaml_content?: string;
}

export interface PromptResponse {
  prompt: string;
  prompt_hash: string;
  generator_version: string;
  schema_version: string;
  schema_hash: string;
}

export interface SchemaValidationResult {
  valid: boolean;
  error?: string;
  schema_hash?: string;
  info?: {
    title?: string;
    description?: string;
  };
  classes?: string[];
  enums?: string[];
}

export interface SetSchemaRequest {
  yaml_content: string;
  version: string;
  schema_type?: string;
  change_reason?: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

async function schemaApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<SchemaAPIResponse<T>> {
  try {
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Add content type for POST/PUT requests
    if (options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const url = API_BASE_URL ? `${API_BASE_URL}${endpoint}` : endpoint;
    console.log(`Schema API request to: ${url}`);

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }

      return {
        data: null as T,
        success: false,
        message: errorMessage,
      };
    }

    if (response.status === 204) {
      return {
        data: null as T,
        success: true,
        message: 'Success',
      };
    }

    const data = await response.json();
    return {
      data,
      success: true,
    };
  } catch (error) {
    console.error('Schema API request error:', error);
    return {
      data: null as T,
      success: false,
      message: error instanceof Error ? error.message : 'Request failed',
    };
  }
}

// ============================================================================
// Schema API
// ============================================================================

export const schemaApi = {
  /**
   * List all available builtin schemas
   */
  async listBuiltin(): Promise<SchemaAPIResponse<BuiltinSchemaInfo[]>> {
    return schemaApiRequest<BuiltinSchemaInfo[]>('/api/schemas/builtin');
  },

  /**
   * Get full details of a builtin schema including YAML content
   */
  async getBuiltin(schemaName: string): Promise<SchemaAPIResponse<BuiltinSchemaDetail>> {
    return schemaApiRequest<BuiltinSchemaDetail>(`/api/schemas/builtin/${schemaName}`);
  },

  /**
   * Get the current schema for an organization
   */
  async getOrganizationSchema(orgId: string): Promise<SchemaAPIResponse<OrganizationSchemaResponse>> {
    return schemaApiRequest<OrganizationSchemaResponse>(`/api/schemas/organization/${orgId}`);
  },

  /**
   * Set a custom schema for an organization
   */
  async setOrganizationSchema(
    orgId: string,
    data: SetSchemaRequest
  ): Promise<SchemaAPIResponse<OrganizationSchemaResponse>> {
    return schemaApiRequest<OrganizationSchemaResponse>(`/api/schemas/organization/${orgId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Assign a builtin schema to an organization
   */
  async setBuiltinSchema(
    orgId: string,
    schemaName: string
  ): Promise<SchemaAPIResponse<OrganizationSchemaResponse>> {
    return schemaApiRequest<OrganizationSchemaResponse>(
      `/api/schemas/organization/${orgId}/set-builtin/${schemaName}`,
      { method: 'POST' }
    );
  },

  /**
   * Generate AI prompt preview from organization's schema
   *
   * @param orgId Organization ID
   * @param includeContext Whether to include ai_prompt_context (default true)
   */
  async generatePrompt(
    orgId: string,
    includeContext: boolean = true
  ): Promise<SchemaAPIResponse<PromptResponse>> {
    const params = new URLSearchParams();
    params.set('include_context', String(includeContext));
    return schemaApiRequest<PromptResponse>(
      `/api/schemas/organization/${orgId}/generate/prompt?${params.toString()}`
    );
  },

  /**
   * Validate a schema without saving it
   */
  async validateSchema(
    yamlContent: string,
    version: string = '1.0.0'
  ): Promise<SchemaAPIResponse<SchemaValidationResult>> {
    return schemaApiRequest<SchemaValidationResult>('/api/schemas/validate', {
      method: 'POST',
      body: JSON.stringify({
        yaml_content: yamlContent,
        version,
      }),
    });
  },

  // ============================================================================
  // Admin Template Management (Admin-only endpoints)
  // ============================================================================

  /**
   * List all global schema templates (Admin only)
   */
  async listTemplates(): Promise<SchemaAPIResponse<GlobalSchemaTemplate[]>> {
    return schemaApiRequest<GlobalSchemaTemplate[]>('/api/schemas/admin/templates');
  },

  /**
   * Get all versions for a specific template (Admin only)
   */
  async getTemplateVersions(name: string): Promise<SchemaAPIResponse<TemplateVersionInfo[]>> {
    return schemaApiRequest<TemplateVersionInfo[]>(`/api/schemas/admin/templates/${name}/versions`);
  },

  /**
   * Create a new global schema template (Admin only)
   */
  async createTemplate(data: CreateTemplateRequest): Promise<SchemaAPIResponse<any>> {
    return schemaApiRequest<any>('/api/schemas/admin/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Create a new version of an existing template (Admin only)
   */
  async createTemplateVersion(
    name: string,
    data: CreateTemplateVersionRequest
  ): Promise<SchemaAPIResponse<any>> {
    return schemaApiRequest<any>(`/api/schemas/admin/templates/${name}/versions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get usage information for a template (which orgs use it) (Admin only)
   */
  async getTemplateUsage(name: string): Promise<SchemaAPIResponse<TemplateUsage>> {
    return schemaApiRequest<TemplateUsage>(`/api/schemas/admin/templates/${name}/usage`);
  },

  /**
   * Assign a template version to organizations (Admin only)
   */
  async assignTemplate(data: AssignTemplateRequest): Promise<SchemaAPIResponse<any>> {
    return schemaApiRequest<any>('/api/schemas/admin/templates/assign', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a custom template (Admin only)
   * Note: Only custom templates can be deleted, not builtin templates
   */
  async deleteTemplate(name: string): Promise<SchemaAPIResponse<any>> {
    return schemaApiRequest<any>(`/api/schemas/admin/templates/${name}`, {
      method: 'DELETE',
    });
  },

  /**
   * Extend a builtin schema with organization-specific additions
   */
  async extendOrganizationSchema(
    orgId: string,
    data: ExtendSchemaRequest
  ): Promise<SchemaAPIResponse<any>> {
    return schemaApiRequest<any>(`/api/schemas/organization/${orgId}/extend-schema`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

export default schemaApi;
